import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthContext } from '@/context/AuthContext';
import { useDashboardHeaderSlot } from '@/context/DashboardHeaderSlotContext';
import { ROUTES, ROLES } from '@/constants';
import { useEscalationHistoryPanelOptional } from '@/features/escalation/context/EscalationHistoryContext';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { LogOut, Bell, ChevronDown, AlertTriangle, Megaphone, EyeOff, Eye, PanelRightClose, PanelRightOpen, Clock3, Users, Shield, BookOpen, FileDown, Network, Menu, PlayCircle, MessageSquare,
    MonitorCog,
} from 'lucide-react';

import { useIsMobile } from '@/hooks/useIsMobile';
import { ThemePalettePicker, ThemePaletteMenuRow, ThemePalettePanel } from '@/components/layout/ThemePalettePicker';
import { ChatButton } from '@/features/chat/components/ChatButton';
import { AiAssistantButton } from '@/features/aiAssistant/components/AiAssistantButton';
import { useChatPanelOptional } from '@/features/chat/context/chatPanelContext';
import { selectCompactHeaderControls, toggleCompactHeaderControls } from '@/store/slices/uiSlice';
import { usePushNotifications } from '@/features/notifications/usePushNotifications';
import { TaskNotificationDetailModal } from '@/features/notifications/TaskNotificationDetailModal';
import { HeaderTitleFlip } from './HeaderTitleFlip';

function pad2(n) {
    return String(n).padStart(2, '0');
}

function formatUtcOffsetLabel(d) {
    const offsetMin = -d.getTimezoneOffset();
    const sign = offsetMin >= 0 ? '+' : '-';
    const abs = Math.abs(offsetMin);
    const h = Math.floor(abs / 60);
    const m = abs % 60;
    if (m === 0) return `UTC${sign}${h}`;
    return `UTC${sign}${h}:${pad2(m)}`;
}

/** Current local wall-clock time (not a countdown); updates every second while mounted. */
function HeaderLiveClock() {
    const [now, setNow] = useState(() => new Date());
    useEffect(() => {
        const sync = () => setNow(new Date());
        sync();
        const id = window.setInterval(sync, 1000);
        const onVisibility = () => {
            if (document.visibilityState === 'visible') sync();
        };
        document.addEventListener('visibilitychange', onVisibility);
        return () => {
            window.clearInterval(id);
            document.removeEventListener('visibilitychange', onVisibility);
        };
    }, []);
    const h24 = now.getHours();
    const h12 = h24 % 12 || 12;
    const ampm = h24 < 12 ? 'AM' : 'PM';
    const timeStr = `${pad2(h12)}:${pad2(now.getMinutes())}:${pad2(now.getSeconds())} ${ampm}`;
    const zoneStr = formatUtcOffsetLabel(now);
    return (
        <div
            className="flex flex-col items-center rounded-md border border-border bg-card px-2.5 py-1 shadow-sm dark:border-green-500/70 dark:shadow-[0_0_10px_0_rgba(34,197,94,0.2)]"
            title="Current local time"
        >
            <time
                dateTime={now.toISOString()}
                className="inline-flex items-center gap-2 font-mono text-sm font-semibold tabular-nums tracking-wide text-foreground dark:[text-shadow:0_0_6px_rgba(34,197,94,0.5)]"
                style={{ fontVariantNumeric: 'tabular-nums', letterSpacing: '0.15em' }}
            >
                <span className="inline-flex items-center gap-1.5" aria-label="Live indicator">
                    <span className="relative inline-flex h-2 w-2">
                        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-500 opacity-60" />
                        <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
                    </span>
                    <span className="text-[10px] font-bold tracking-[0.25em] text-emerald-600 dark:text-emerald-400">
                        LIVE
                    </span>
                </span>
                {timeStr}
            </time>
            <span className="text-[10px] font-medium leading-tight text-muted-foreground">{zoneStr}</span>
        </div>
    );
}

function MobileChatMenuRow({ onNavigateAway }) {
    const panel = useChatPanelOptional();
    if (!panel) return null;
    return (
        <button
            type="button"
            onClick={() => {
                onNavigateAway();
                panel.togglePanel();
            }}
            className="flex w-full items-center gap-2 px-4 py-2 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
            <MessageSquare className="h-3.5 w-3.5" aria-hidden />
            Chat
        </button>
    );
}

export function FilterPill({ value, onChange, options, triggerClassName = '', menuClassName = '' }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    const activeLabel = options.find((o) => o.value === value)?.label ?? value;

    useEffect(() => {
        const handler = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    return (
        <div ref={ref} className="relative max-w-full">
            <button
                type="button"
                onClick={() => setOpen((prev) => !prev)}
                className={`flex max-w-full items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1 text-xs font-semibold text-foreground shadow-sm transition hover:border-primary-300 ${triggerClassName}`}
            >
                <span className="truncate">{activeLabel}</span>
                <ChevronDown className={`h-3 w-3 flex-shrink-0 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`} />
            </button>

            <div className={`absolute left-0 top-full z-50 mt-1 min-w-full max-h-72 max-w-[min(100vw-3rem,20rem)] overflow-y-auto whitespace-nowrap rounded-lg border border-border bg-card py-1 shadow-lg transition-all ${open ? 'visible opacity-100' : 'invisible opacity-0'} ${menuClassName}`}>
                {options.map((o) => (
                    <button
                        key={o.value}
                        type="button"
                        onClick={() => { onChange(o.value); setOpen(false); }}
                        className={`flex w-full items-center px-4 py-2 text-left text-sm transition-colors hover:bg-primary-50 dark:hover:bg-primary-950/40 ${o.value === value ? 'font-semibold text-primary-700 dark:text-primary-300' : 'text-muted-foreground'}`}
                    >
                        {o.label}
                    </button>
                ))}
            </div>
        </div>
    );
}

/**
 * Top app bar shared by Executive, Task Force Chair, Pillar Chair, and Member layouts (C32 brand, optional sidebar control, center filters, bell, user menu).
 */
export function ExecutiveAppHeader({
    leading = null,
    title,
    subtitle = null,
    center = null,
    unreadNotifications: unreadFromProp,
    showChatButton = true,
    showAiAssistant = true,
    showNotifications = true,
    manualHelpHref = null,
    /** Shells without a sidebar (Executive) surface Export here instead. */
    exportHref = null,
    /** Same reason as `exportHref`: no sidebar to hang Structure off. */
    structureHref = null,
    /**
     * Same reason again. Null unless the account holds a dashboard grant, so
     * passing it is itself the gate — this component does no permission
     * checking of its own, and the four layouts that share it must keep it
     * that way.
     */
    operationalDashboardHref = null,
    /**
     * Shows the event countdown inside the HUD ornament. Same convention as
     * `operationalDashboardHref`: passing it is the gate. This component does
     * no role checking of its own, and the six layouts that share it must keep
     * it that way. On for the three leadership shells today.
     */
    showCountdown = false,
    onOpenSidebar = null,
}) {
    const { user, logout } = useAuthContext();
    /**
     * Executive keeps a deliberately bare header bar: Structure, Export, User
     * Manual and Theme move out of it and live in the profile menu instead,
     * beside Hide controls and Escalation History. Executive is the only shell
     * with no sidebar, so its bar was carrying every entry point at once.
     * Every other role keeps the icons exactly where they were.
     */
    const isExecutive = user?.role === ROLES.EXECUTIVE;
    const escalationHistoryPath =
        {
            [ROLES.MEMBER]: ROUTES.MEMBER_ESCALATION_HISTORY,
            [ROLES.PROJECT_MANAGER]: ROUTES.PROJECT_MANAGER_ESCALATION_HISTORY,
            [ROLES.PILLAR_CHAIR]: ROUTES.PILLAR_CHAIR_ESCALATION_HISTORY,
            [ROLES.TASK_FORCE_CHAIR]: ROUTES.TASK_FORCE_CHAIR_ESCALATION_HISTORY,
        }[user?.role] ?? null;
    const { items: pushBellItems, markAllRead: markAllPushRead } = usePushNotifications();
    const { unreadNotifications: unreadFromContext, requestCriticalAlertBellFocus } = useDashboardHeaderSlot();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const escalationHistoryPanel = useEscalationHistoryPanelOptional();
    const compactHeaderControls = useAppSelector(selectCompactHeaderControls);
    const notifRef = useRef(null);
    const [notifOpen, setNotifOpen] = useState(false);
    const [taskDetailNotification, setTaskDetailNotification] = useState(null);
    const isMobile = useIsMobile();
    const profileRef = useRef(null);
    const [profileOpen, setProfileOpen] = useState(false);
    const [themePanelOpen, setThemePanelOpen] = useState(false);

    const notifications = useMemo(() => {
        const extra = Array.isArray(unreadFromProp)
            ? unreadFromProp
            : Array.isArray(unreadFromContext)
              ? unreadFromContext
              : [];
        return [...pushBellItems, ...extra].slice(0, 50);
    }, [pushBellItems, unreadFromProp, unreadFromContext]);
    const unreadCount = notifications.length;
    const badgeText = unreadCount > 99 ? '99+' : String(unreadCount);

    useEffect(() => {
        const onDoc = (e) => {
            if (notifRef.current && !notifRef.current.contains(e.target)) setNotifOpen(false);
        };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, []);

    useEffect(() => {
        if (!notifOpen) return;
        const onKey = (e) => {
            if (e.key === 'Escape') setNotifOpen(false);
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [notifOpen]);

    useEffect(() => {
        if (!profileOpen) return;
        const onDoc = (e) => {
            if (profileRef.current && !profileRef.current.contains(e.target)) setProfileOpen(false);
        };
        const onKey = (e) => {
            if (e.key === 'Escape') setProfileOpen(false);
        };
        document.addEventListener('mousedown', onDoc);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDoc);
            document.removeEventListener('keydown', onKey);
        };
    }, [profileOpen]);

    const initials = (user?.username || 'U').slice(0, 2).toUpperCase();
    const dashboardLabel = title || 'Executive Dashboard';
    const openTaskNotificationModalForLeadership =
        user?.role && user.role !== ROLES.MEMBER;

    return (
        <header
            className="relative flex h-20 flex-shrink-0 items-center justify-between gap-3 rounded-lg border border-primary-700/70 bg-card px-4 sm:px-6"
            style={{
                boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)',
            }}
        >
            <span
                className="pointer-events-none absolute left-0 top-0 h-4 w-4 border-l-2 border-t-2 border-primary-700"
                aria-hidden
            />
            <span
                className="pointer-events-none absolute right-0 top-0 h-4 w-4 border-r-2 border-t-2 border-primary-700"
                aria-hidden
            />
            <span
                className="pointer-events-none absolute bottom-0 left-0 h-4 w-4 border-b-2 border-l-2 border-primary-700"
                aria-hidden
            />
            <span
                className="pointer-events-none absolute bottom-0 right-0 h-4 w-4 border-b-2 border-r-2 border-primary-700"
                aria-hidden
            />
            <div className="flex min-w-0 flex-shrink-0 items-center gap-3">
                {onOpenSidebar ? (
                    <button
                        type="button"
                        onClick={onOpenSidebar}
                        className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground md:hidden"
                        aria-label="Open navigation menu"
                    >
                        <Menu className="h-5 w-5" aria-hidden />
                    </button>
                ) : null}
                {leading}
                <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md bg-primary-700 text-[0.625rem] font-bold tracking-wider text-white">
                    C32
                </div>
                <div className="min-w-0">
                    <div className="truncate font-header text-xl font-black uppercase leading-none tracking-[0.22em] text-primary-700 sm:text-[1.35rem] dark:text-primary-400">
                        COP32
                    </div>
                    {subtitle && !isMobile ? (
                        <div className="truncate text-xs font-medium text-muted-foreground" title={subtitle}>
                            {subtitle}
                        </div>
                    ) : null}
                </div>
            </div>

            {/* HUD Center Section — original span (65vw / 56rem); title above ornament so glyphs never cover the graphic */}
            <div
                className="pointer-events-none absolute left-1/2 top-0 hidden h-full w-[min(60vw,75rem)] -translate-x-1/2 flex-col items-center justify-center px-2 md:flex"
            >
                <div className="w-full max-w-full min-w-0 translate-y-2 px-1 text-center leading-tight">
                    <HeaderTitleFlip title={dashboardLabel} showCountdown={showCountdown} />
                </div>
                <svg
                    className="pointer-events-none h-10 w-full shrink-0 overflow-visible sm:h-12 -mt-1"
                    viewBox="0 10 1000 120"
                    preserveAspectRatio="none"
                    fill="none"
                    aria-hidden
                >
                    <defs>
                        <filter id="exec-hud-glow" x="-2%" y="-50%" width="104%" height="200%">
                            <feGaussianBlur in="SourceGraphic" stdDeviation="2" result="blur" />
                            <feMerge>
                                <feMergeNode in="blur" />
                                <feMergeNode in="SourceGraphic" />
                            </feMerge>
                        </filter>
                        <filter id="exec-hud-shadow" x="-10%" y="-10%" width="120%" height="160%">
                            <feDropShadow dx="0" dy="3" stdDeviation="3" floodColor="var(--primary-700)" floodOpacity="0.10" />
                        </filter>
                        <linearGradient id="exec-hud-fade-l" gradientUnits="userSpaceOnUse" x1="0" y1="50" x2="72" y2="50">
                            <stop offset="0%" stopColor="var(--primary-500)" stopOpacity="0" />
                            <stop offset="100%" stopColor="var(--primary-500)" stopOpacity="1" />
                        </linearGradient>
                        <linearGradient id="exec-hud-fade-r" gradientUnits="userSpaceOnUse" x1="928" y1="50" x2="1000" y2="50">
                            <stop offset="0%" stopColor="var(--primary-500)" stopOpacity="1" />
                            <stop offset="100%" stopColor="var(--primary-500)" stopOpacity="0" />
                        </linearGradient>
                    </defs>

                    <line x1="0" y1="50" x2="72" y2="50"
                          stroke="url(#exec-hud-fade-l)" strokeWidth="1.5" vectorEffect="non-scaling-stroke" />

                    <path
                        d="M 72,50 L 380,106 L 620,106 L 928,50"
                        stroke="var(--primary-500)"
                        strokeWidth="1.5"
                        vectorEffect="non-scaling-stroke"
                        filter="url(#exec-hud-glow)"
                    />
                    <path
                        d="M 72,50 L 380,106 L 620,106 L 928,50 Z"
                        fill="color-mix(in srgb, var(--primary-500) 6%, transparent)"
                        filter="url(#exec-hud-shadow)"
                    />

                    <circle cx="72" cy="50" r="2.5" fill="var(--primary-400)" filter="url(#exec-hud-glow)" />
                    <circle cx="928" cy="50" r="2.5" fill="var(--primary-400)" filter="url(#exec-hud-glow)" />

                    <line x1="928" y1="50" x2="1000" y2="50"
                          stroke="url(#exec-hud-fade-r)" strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
                </svg>
            </div>

            <div className="flex flex-shrink-0 items-center gap-2 sm:gap-4">
                {center && !compactHeaderControls && !isMobile ? (
                    <div className="max-w-[28vw] min-w-0">
                        {center}
                    </div>
                ) : null}
                {structureHref && !isMobile && !isExecutive ? (
                    <Link
                        to={structureHref}
                        className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                        aria-label="Open Structure"
                        title="Structure"
                    >
                        <Network className="h-5 w-5" aria-hidden />
                    </Link>
                ) : null}
                {exportHref && !isMobile && !isExecutive ? (
                    <Link
                        to={exportHref}
                        className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                        aria-label="Open Export"
                        title="Export"
                    >
                        <FileDown className="h-5 w-5" aria-hidden />
                    </Link>
                ) : null}
                {operationalDashboardHref && !isMobile && !isExecutive ? (
                    <Link
                        to={operationalDashboardHref}
                        className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                        aria-label="Open Operational Dashboard"
                        title="Operational Dashboard"
                    >
                        <MonitorCog className="h-5 w-5" aria-hidden />
                    </Link>
                ) : null}
                {manualHelpHref && !isMobile && !isExecutive ? (
                    <Link
                        to={manualHelpHref}
                        className="rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                        aria-label="Open User Manual"
                        title="User Manual"
                    >
                        <BookOpen className="h-5 w-5" aria-hidden />
                    </Link>
                ) : null}
                {isMobile || isExecutive ? null : <ThemePalettePicker />}
                {compactHeaderControls || !showChatButton || isMobile ? null : <ChatButton />}
                {/* Executive has no sidebar, so the header is its only entry point. */}
                {compactHeaderControls || !showAiAssistant || isMobile ? null : <AiAssistantButton />}
                {compactHeaderControls && !isMobile ? <HeaderLiveClock /> : null}
                {(!isMobile && compactHeaderControls) || !showNotifications ? null : (
                <div ref={notifRef} className="relative">
                    <button
                        type="button"
                        onClick={() => setNotifOpen((o) => !o)}
                        className="relative rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                        aria-label={
                            unreadCount > 0
                                ? `Notifications, ${unreadCount} unread`
                                : 'Notifications'
                        }
                        aria-expanded={notifOpen}
                        aria-haspopup="dialog"
                    >
                        <Bell
                            className={`h-5 w-5 ${unreadCount > 0 ? 'text-foreground' : ''}`}
                            aria-hidden
                        />
                        {unreadCount > 0 ? (
                            <span className="absolute -right-px -top-px inline-flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-bold tabular-nums leading-none text-white shadow-sm ring-1 ring-card">
                                {badgeText}
                            </span>
                        ) : null}
                    </button>

                    <div
                        className={`absolute right-0 top-full z-[60] mt-1.5 w-[min(calc(100vw-2rem),20rem)] origin-top-right rounded-xl border border-border bg-card shadow-lg transition-all ${
                            notifOpen
                                ? 'visible scale-100 opacity-100'
                                : 'invisible scale-95 opacity-0 pointer-events-none'
                        }`}
                        role="dialog"
                        aria-label="Notifications"
                    >
                        <div className="flex items-center justify-between border-b border-border px-3 py-2.5">
                            <span className="text-xs font-bold uppercase tracking-wide text-foreground">
                                Notifications
                            </span>
                            <div className="flex items-center gap-2">
                                {unreadCount > 0 ? (
                                    <>
                                        <span className="rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-bold tabular-nums text-red-700 dark:bg-red-950/50 dark:text-red-300">
                                            {unreadCount} unread
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() => { markAllPushRead(); }}
                                            className="text-[10px] font-semibold text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-200"
                                        >
                                            Mark all read
                                        </button>
                                    </>
                                ) : null}
                            </div>
                        </div>
                        <div className="max-h-72 overflow-y-auto py-1">
                            {notifications.length === 0 ? (
                                <p className="px-3 py-6 text-center text-sm text-muted-foreground">
                                    No unread notifications.
                                </p>
                            ) : (
                                <ul className="space-y-0.5 px-1 pb-1">
                                    {notifications.map((n) => {
                                        const isEscalation = n.kind === 'escalation';
                                        const isChat = n.kind === 'chat' && n.taskId;
                                        const isOverdue = n.kind === 'overdue' && n.taskId;
                                        const isTaskStarted = n.kind === 'task_started' && n.taskId;
                                        const isSubmission = n.kind === 'submission' && n.taskId;
                                        const isCriticalFailure = n.kind === 'critical_failure';

                                        const handleClick = () => {
                                            const rawFocus =
                                                n.alertFocusId != null && String(n.alertFocusId).trim() !== ''
                                                    ? String(n.alertFocusId)
                                                    : n.id != null && n.id !== ''
                                                      ? String(n.id)
                                                      : '';
                                            if (
                                                (n.kind === 'critical' || isCriticalFailure)
                                                && rawFocus !== ''
                                            ) {
                                                requestCriticalAlertBellFocus(rawFocus);
                                                if (typeof n.onRead === 'function') n.onRead();
                                                setNotifOpen(false);
                                                return;
                                            }
                                            // Escalation notices land on the caller's own escalation
                                            // history; the task-scoped ones open the task itself.
                                            if (isEscalation && escalationHistoryPath) {
                                                navigate(escalationHistoryPath);
                                                if (typeof n.onRead === 'function') n.onRead();
                                                setNotifOpen(false);
                                                return;
                                            }
                                            if (isChat || isOverdue || isTaskStarted || isSubmission) {
                                                if (openTaskNotificationModalForLeadership) {
                                                    setTaskDetailNotification(n);
                                                    setNotifOpen(false);
                                                    return;
                                                }
                                                navigate(ROUTES.MEMBER_TASK_DETAIL(n.taskId));
                                                setNotifOpen(false);
                                            }

                                            if (typeof n.onRead === 'function') {
                                                n.onRead();
                                            }
                                        };

                                        return (
                                        <li key={n.id}>
                                            <button
                                                type="button"
                                                onClick={handleClick}
                                                className="flex w-full gap-2 rounded-lg px-2 py-2 text-left transition-colors hover:bg-muted/80"
                                            >
                                                <span
                                                    className={`mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full ${
                                                        isEscalation
                                                            ? 'bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300'
                                                            : isCriticalFailure
                                                              ? 'bg-orange-50 text-orange-600 dark:bg-orange-950/40 dark:text-orange-300'
                                                              : isTaskStarted
                                                                ? 'bg-primary-50 text-primary-700 dark:bg-primary-950/40 dark:text-primary-300'
                                                                : 'bg-red-50 text-red-600 dark:bg-red-950/40 dark:text-red-300'
                                                    }`}
                                                >
                                                    {isEscalation ? (
                                                        <Megaphone className="h-3.5 w-3.5" aria-hidden />
                                                    ) : isTaskStarted ? (
                                                        <PlayCircle className="h-3.5 w-3.5" aria-hidden />
                                                    ) : (
                                                        <AlertTriangle className="h-3.5 w-3.5" aria-hidden />
                                                    )}
                                                </span>
                                                <div className="min-w-0 flex-1">
                                                    <p className="text-sm font-semibold leading-snug text-foreground">
                                                        {n.title}
                                                    </p>
                                                    {n.subtitle ? (
                                                        <p className="mt-0.5 text-xs text-muted-foreground">
                                                            {n.subtitle}
                                                        </p>
                                                    ) : null}
                                                    {n.meta ? (
                                                        <p
                                                            className={`mt-1 text-[10px] font-medium uppercase tracking-wide ${
                                                                isEscalation
                                                                    ? 'text-amber-800/90'
                                                                    : isCriticalFailure
                                                                      ? 'text-orange-600/90'
                                                                      : isTaskStarted
                                                                        ? 'text-primary-700/90'
                                                                        : 'text-red-600/90'
                                                            }`}
                                                        >
                                                            {n.meta}
                                                        </p>
                                                    ) : null}
                                                </div>
                                            </button>
                                        </li>
                                        );
                                    })}
                                </ul>
                            )}
                        </div>
                    </div>
                </div>
                )}

                {compactHeaderControls && !isMobile ? (
                    <button
                        type="button"
                        onClick={() => dispatch(toggleCompactHeaderControls())}
                        className="rounded-md p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring/40"
                        aria-label="Show controls"
                        title="Show controls"
                    >
                        <PanelRightOpen className="h-4 w-4" aria-hidden />
                    </button>
                ) : null}

                {!isMobile && compactHeaderControls ? null : (
                <div ref={profileRef} className="relative flex items-center gap-2 border-l border-border pl-2">
                    <button
                        type="button"
                        onClick={() => setProfileOpen((v) => !v)}
                        className="flex cursor-pointer items-center gap-2 rounded-md focus:outline-none focus:ring-2 focus:ring-ring/40"
                        aria-haspopup="menu"
                        aria-expanded={profileOpen}
                        aria-label="Open user menu"
                    >
                        <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary-100 text-xs font-semibold text-primary-700">
                            {initials}
                        </div>
                        <span className="hidden max-w-[8rem] truncate text-sm font-medium text-foreground sm:inline">
                            {user?.username || 'User'}
                        </span>
                        <ChevronDown className={`h-3.5 w-3.5 flex-shrink-0 text-muted-foreground transition-transform ${profileOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {profileOpen ? (
                    <div
                        role="menu"
                        className="absolute right-0 top-full z-50 mt-1 min-w-44 rounded-lg border border-border bg-card py-1 shadow-lg"
                    >
                        <div className="border-b border-border px-4 py-2.5">
                            <p className="text-sm font-semibold text-foreground">{user?.username || 'User'}</p>
                            <p className="text-xs text-muted-foreground">{user?.email || ''}</p>
                        </div>
                        <button
                            type="button"
                            onClick={() => {
                                const roleBase = {
                                    [ROLES.ADMIN]: ROUTES.ADMIN,
                                    [ROLES.EXECUTIVE]: ROUTES.EXECUTIVE,
                                    [ROLES.TASK_FORCE_CHAIR]: ROUTES.TASK_FORCE_CHAIR,
                                    [ROLES.PILLAR_CHAIR]: ROUTES.PILLAR_CHAIR,
                                    [ROLES.PROJECT_MANAGER]: ROUTES.PROJECT_MANAGER,
                                    [ROLES.MEMBER]: ROUTES.MEMBER,
                                }[user?.role] || '/member';
                                setProfileOpen(false);
                                navigate(`${roleBase}/settings/profile`);
                            }}
                            className="flex w-full items-center gap-2 px-4 py-2 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                        >
                            <Users className="h-3.5 w-3.5" aria-hidden />
                            Update Profile
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                const roleBase = {
                                    [ROLES.ADMIN]: ROUTES.ADMIN,
                                    [ROLES.EXECUTIVE]: ROUTES.EXECUTIVE,
                                    [ROLES.TASK_FORCE_CHAIR]: ROUTES.TASK_FORCE_CHAIR,
                                    [ROLES.PILLAR_CHAIR]: ROUTES.PILLAR_CHAIR,
                                    [ROLES.PROJECT_MANAGER]: ROUTES.PROJECT_MANAGER,
                                    [ROLES.MEMBER]: ROUTES.MEMBER,
                                }[user?.role] || '/member';
                                setProfileOpen(false);
                                navigate(`${roleBase}/settings/security`);
                            }}
                            className="flex w-full items-center gap-2 px-4 py-2 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                        >
                            <Shield className="h-3.5 w-3.5" aria-hidden />
                            Change Password
                        </button>

                        <div className="my-1 border-t border-border" />

                        {isMobile || isExecutive ? (
                            <>
                                {structureHref ? (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setProfileOpen(false);
                                            navigate(structureHref);
                                        }}
                                        className="flex w-full items-center gap-2 px-4 py-2 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                                    >
                                        <Network className="h-3.5 w-3.5" aria-hidden />
                                        Structure
                                    </button>
                                ) : null}
                                {exportHref ? (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setProfileOpen(false);
                                            navigate(exportHref);
                                        }}
                                        className="flex w-full items-center gap-2 px-4 py-2 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                                    >
                                        <FileDown className="h-3.5 w-3.5" aria-hidden />
                                        Export
                                    </button>
                                ) : null}
                                {operationalDashboardHref ? (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setProfileOpen(false);
                                            navigate(operationalDashboardHref);
                                        }}
                                        className="flex w-full items-center gap-2 px-4 py-2 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                                    >
                                        <MonitorCog className="h-3.5 w-3.5" aria-hidden />
                                        Operational Dashboard
                                    </button>
                                ) : null}
                                {manualHelpHref ? (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setProfileOpen(false);
                                            navigate(manualHelpHref);
                                        }}
                                        className="flex w-full items-center gap-2 px-4 py-2 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                                    >
                                        <BookOpen className="h-3.5 w-3.5" aria-hidden />
                                        User Manual
                                    </button>
                                ) : null}
                                {isMobile && showChatButton ? (
                                    <MobileChatMenuRow onNavigateAway={() => setProfileOpen(false)} />
                                ) : null}
                                <ThemePaletteMenuRow
                                    onOpenPanel={() => {
                                        setProfileOpen(false);
                                        setThemePanelOpen(true);
                                    }}
                                />
                                {/* Executive reads these six as one list, so the
                                    rule that separates them from Hide controls
                                    is only drawn on mobile. */}
                                {isExecutive ? null : (
                                    <div className="my-1 border-t border-border" />
                                )}
                            </>
                        ) : null}

                        {isMobile ? null : (
                            <button
                                type="button"
                                onClick={() => dispatch(toggleCompactHeaderControls())}
                                className="flex w-full items-center gap-2 px-4 py-2 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                            >
                                {compactHeaderControls ? (
                                    <PanelRightOpen className="h-3.5 w-3.5" aria-hidden />
                                ) : (
                                    <PanelRightClose className="h-3.5 w-3.5" aria-hidden />
                                )}
                                {compactHeaderControls ? 'Show controls' : 'Hide controls'}
                            </button>
                        )}
                        {escalationHistoryPanel ? (
                            <button
                                type="button"
                                onClick={() => {
                                    setProfileOpen(false);
                                    escalationHistoryPanel.openPanel();
                                }}
                                className="flex w-full items-center gap-2 px-4 py-2 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                            >
                                <Clock3 className="h-3.5 w-3.5" aria-hidden />
                                Escalation History
                            </button>
                        ) : null}
                        <div className="my-1 border-t border-border" />
                        <button
                            type="button"
                            onClick={() => {
                                setProfileOpen(false);
                                logout();
                                navigate(ROUTES.HOME, { replace: true });
                            }}
                            className="flex w-full items-center gap-2 px-4 py-2 text-sm text-muted-foreground transition-colors hover:bg-danger/10 hover:text-danger"
                        >
                            <LogOut className="h-3.5 w-3.5" />
                            Logout
                        </button>
                    </div>
                    ) : null}
                </div>
                )}
            </div>
            {isMobile || isExecutive ? (
                <ThemePalettePanel
                    open={themePanelOpen}
                    onClose={() => setThemePanelOpen(false)}
                    anchor="fixed"
                />
            ) : null}
            {taskDetailNotification ? (
                <TaskNotificationDetailModal
                    notification={taskDetailNotification}
                    open={Boolean(taskDetailNotification)}
                    onClose={() => setTaskDetailNotification(null)}
                    userId={user?.userId}
                    userRole={user?.role}
                />
            ) : null}
        </header>
    );
}

export default ExecutiveAppHeader;
